import { computed, inject, Injector, linkedSignal, resource, runInInjectionContext, signal } from '@angular/core';
import { ConnectivityService } from '@okcargo-apps/shared-infrastructure';
import {
  IInfiniteScrollCollection,
  IInfiniteScrollCollectionOptions,
  IInfiniteScrollCollectionVm,
  INFINITE_SCROLL_LOADING_DELAY_MS,
  InfiniteScrollRefreshSource,
} from '../../models/infinite-scroll-collection.model';
import {
  emptyInfiniteScrollState,
  emptyInfiniteScrollVm,
  IInfiniteScrollLoadedResult,
  IInfiniteScrollRequestState,
  IInfiniteScrollState,
} from './infinite-scroll.model';

function delay(ms: number, abortSignal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timeout = setTimeout(resolve, ms);
    abortSignal.addEventListener(
      'abort',
      () => {
        clearTimeout(timeout);
        resolve();
      },
      { once: true },
    );
  });
}

export function createInfiniteScroll<TQuery, T>(
  options: IInfiniteScrollCollectionOptions<TQuery, T>,
): IInfiniteScrollCollection<T> {
  const injector = inject(Injector);
  const connectivity = inject(ConnectivityService);
  const loadingDelayMs = options.loadingDelayMs ?? inject(INFINITE_SCROLL_LOADING_DELAY_MS);

  const queryKey = computed<string | null>(() => {
    const query = options.query();
    const pageSize = options.pageSize;
    return query === null ? null : JSON.stringify({ query, pageSize });
  });

  const pageIndex = linkedSignal<string | null, number>({
    source: () => queryKey(),
    computation: () => 0,
  });

  const refreshNonce = signal(0);

  const requestState = computed<IInfiniteScrollRequestState<TQuery> | null>(() => {
    const query = options.query();
    const key = queryKey();
    if (query === null || key === null) return null;
    return {
      requestId: `${key}#${pageIndex()}#${refreshNonce()}`,
      queryKey: key,
      query,
      pageIndex: pageIndex(),
      pageSize: options.pageSize,
    };
  });

  let loading = false;

  const resourceRef = runInInjectionContext(injector, () =>
    resource({
      params: () => requestState(),
      loader: async ({ params, abortSignal }): Promise<IInfiniteScrollLoadedResult<T>> => {
        try {
          if (!params) return { items: [], totalElements: 0, loadedAt: new Date(), requestId: '' };
          const [result] = await Promise.all([
            options.loadPage(params.query, params.pageIndex, params.pageSize, abortSignal),
            delay(loadingDelayMs, abortSignal),
          ]);
          return { ...result, loadedAt: new Date(), requestId: params.requestId };
        } finally {
          loading = false;
        }
      },
    }),
  );

  const state = linkedSignal<
    { request: IInfiniteScrollRequestState<TQuery> | null; result: IInfiniteScrollLoadedResult<T> | undefined },
    IInfiniteScrollState<T>
  >({
    source: () => ({
      request: requestState(),
      // On error the fresh result is DISCARDED, not kept: this is a paginated accumulation, so
      // merging a previous result again would duplicate the page. `keepLastResolvedValue`
      // (layout-ionic) solves the opposite case, the single entity a view is showing.
      result: resourceRef.status() === 'error' ? undefined : resourceRef.value(),
    }),
    computation: ({ request, result }, previous) => {
      const prevState = previous?.value;
      if (!request) {
        return options.keepDataOnIdle && prevState ? prevState : emptyInfiniteScrollState<T>();
      }

      const queryChanged = !prevState || prevState.queryKey !== request.queryKey;
      if (queryChanged) {
        const fresh = emptyInfiniteScrollState<T>();
        fresh.queryKey = request.queryKey;
        if (result && result.requestId === request.requestId) {
          fresh.pages.set(request.pageIndex, { items: result.items, loadedAt: result.loadedAt });
          fresh.totalElements = result.totalElements;
          fresh.lastLoadedAt = result.loadedAt;
        }
        return fresh;
      }

      const resultIsCurrent = !!result && result.requestId === request.requestId;
      const pages = new Map(prevState.pages);
      const next: IInfiniteScrollState<T> = {
        queryKey: request.queryKey,
        pages,
        totalElements: prevState.totalElements,
        isStale: prevState.isStale,
        staleSince: prevState.staleSince,
        lastLoadedAt: prevState.lastLoadedAt,
      };

      if (resultIsCurrent) {
        pages.set(request.pageIndex, { items: result.items, loadedAt: result.loadedAt });
        next.totalElements = result.totalElements;
        next.lastLoadedAt = result.loadedAt;
      }

      return next;
    },
  });

  const vm = computed<IInfiniteScrollCollectionVm<T>>(() => {
    const request = requestState();
    if (!request) return emptyInfiniteScrollVm<T>();

    const current = state();
    const pageIndices = Array.from(current.pages.keys()).sort((a, b) => a - b);
    let items: T[] = pageIndices.flatMap((index) => current.pages.get(index)?.items ?? []);
    if (options.getItemKey) {
      const keyFn = options.getItemKey;
      const seen = new Map<string, T>();
      for (const item of items) {
        seen.set(keyFn(item), item);
      }
      items = Array.from(seen.values());
    }

    const isLoading = resourceRef.isLoading();
    const isLoadingMore = isLoading && request.pageIndex > 0;

    return {
      items,
      totalElements: current.totalElements,
      isLoading: isLoading && !isLoadingMore,
      isLoadingMore,
      error: resourceRef.error(),
      hasMore: items.length < current.totalElements,
      hasLoadedMore: current.pages.size > 1,
      isStale: current.isStale,
      lastLoadedAt: current.lastLoadedAt,
      staleSince: current.staleSince,
    };
  });

  function loadNextPage(): void {
    if (connectivity.isOffline()) return;
    if (loading) return;
    if (resourceRef.error()) return;
    if (!vm().hasMore) return;
    loading = true;
    pageIndex.update((index) => index + 1);
  }

  function refresh(source: InfiniteScrollRefreshSource): void {
    if (connectivity.isOffline()) return;
    if (requestState() === null) return;
    if (source === 'user') {
      loading = true;
      state.update((current) => ({
        ...current,
        pages: new Map(),
        totalElements: 0,
        isStale: false,
        staleSince: null,
        lastLoadedAt: null,
      }));
      pageIndex.set(0);
      refreshNonce.update((nonce) => nonce + 1);
      return;
    }

    if (state().pages.size <= 1) {
      if (loading) return;
      loading = true;
      resourceRef.reload();
      return;
    }

    state.update((current) => ({ ...current, isStale: true, staleSince: new Date() }));
  }

  function retry(): void {
    if (connectivity.isOffline()) return;
    if (loading) return;
    if (!resourceRef.error()) return;
    loading = true;
    resourceRef.reload();
  }

  return { vm, loadNextPage, refresh, retry };
}
